// Thin wrapper over the PostHog snippet loaded in index.html.
// All calls are best-effort: never throw, never block.

type PostHog = {
  capture: (event: string, props?: Record<string, unknown>) => void
  identify: (distinctId: string, props?: Record<string, unknown>) => void
  reset: () => void
  register: (props: Record<string, unknown>) => void
  setPersonProperties?: (props: Record<string, unknown>) => void
  get_distinct_id?: () => string
}

function ph(): PostHog | null {
  if (typeof window === 'undefined') return null
  const p = (window as unknown as { posthog?: PostHog }).posthog
  return p && typeof p.capture === 'function' ? p : null
}

export function captureEvent(event: string, props?: Record<string, unknown>): void {
  try {
    ph()?.capture(event, props)
  } catch { /* swallow */ }
}

export function identifyUser(user: {
  id: string
  email: string
  billing_plan: 'free' | 'plus' | 'max'
}): void {
  try {
    ph()?.identify(user.id, {
      email: user.email,
      plan: user.billing_plan,
    })
    ph()?.register({ plan: user.billing_plan })
  } catch { /* swallow */ }
}

export function resetUser(): void {
  try {
    ph()?.reset()
  } catch { /* swallow */ }
}

// Set durable person properties (e.g. user_segment) for cohort analytics.
export function setPersonProperties(props: Record<string, unknown>): void {
  try {
    const p = ph()
    if (p?.setPersonProperties) p.setPersonProperties(props)
    else if (p?.get_distinct_id) p.identify(p.get_distinct_id(), props)
  } catch { /* swallow */ }
}

// Funnel: marketing cta_click → oauth_redirect_initiated → oauth_callback_success
const POST_OAUTH_FLAG = 'rundown:post_oauth_expected'

export function markOauthRedirectInitiated(): void {
  try {
    sessionStorage.setItem(POST_OAUTH_FLAG, String(Date.now()))
  } catch { /* sessionStorage unavailable */ }
  captureEvent('oauth_redirect_initiated', { provider: 'google' })
}

export function consumeOauthCallbackIfPending(): { wasFreshAuth: boolean; latencyMs?: number } {
  let started: string | null = null
  try {
    started = sessionStorage.getItem(POST_OAUTH_FLAG)
    if (started) sessionStorage.removeItem(POST_OAUTH_FLAG)
  } catch { /* sessionStorage unavailable */ }
  if (!started) return { wasFreshAuth: false }
  const startedMs = Number(started)
  const latencyMs = Number.isFinite(startedMs) ? Date.now() - startedMs : undefined
  return { wasFreshAuth: true, latencyMs }
}
